import React, { useRef, useState } from "react";
import { ShallowEntity } from "../types";
import EntityIcon from "../Components/EntityIcon";
import PendingReviewBadge from "../Components/PendingReviewBadge";
import { useEntityNavigate } from "../entityNavigation";
import { Link } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { useCardHydration } from "../Components/useCardHydration";
import { prefetchAttrs } from "../../Data/visibilityPrefetch";
import { entityKindMap } from "../utils";
import {
  SPECIAL_PERSON_ATTRIBUTE,
  SPECIAL_VEHICLE_ATTRIBUTE,
} from "../Components/EntitySearchInput";
import { generateSubheader } from "../Panels/EntityRelationshipsPanel";
import type { EntityCellValue, FactValue } from "../types";

// Above the portalled search dropdown (`z-[9999]` in EntitySearchInput),
// which is where the list variant usually renders.
const CLIPPED_NAME_TOOLTIP_Z_INDEX = 10000;

// Shows the full name on hover only when the name is cut off (#12331). A
// list row has no column to widen, so without it two funds sharing a long
// prefix read the same; a name that fits gets no tooltip, since repeating
// visible text on hover is noise (#11426's hover ruling).
const ClippedListName = ({
  name,
  children,
}: {
  name: string;
  children: React.ReactNode;
}) => {
  const nameRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <Tooltip
      title={name}
      placement="top-start"
      disableInteractive
      open={open}
      onOpen={() => {
        const node = nameRef.current;
        setOpen(node !== null && node.scrollWidth > node.clientWidth);
      }}
      onClose={() => setOpen(false)}
      slotProps={{ popper: { sx: { zIndex: CLIPPED_NAME_TOOLTIP_Z_INDEX } } }}
    >
      <span ref={nameRef} className="text-sm text-gray-800 truncate block">
        {children}
      </span>
    </Tooltip>
  );
};

interface EntityCardProps {
  entity: ShallowEntity;
  subheader?: string | React.ReactNode;
  variant?: "card" | "inline" | "list";
  disableLink?: boolean;
  prefetchedFacts?: Record<string, any> | null; // Optional pre-fetched facts from search results
  hideIcon?: boolean; // Hide the entity icon circle (e.g. in compact dropdown results)
}

const EntityCard = ({
  entity,
  subheader,
  variant = "card",
  disableLink,
  prefetchedFacts,
  hideIcon,
}: EntityCardProps) => {
  const entityNavigate = useEntityNavigate();

  // Determine what facts to fetch based on entity type
  const factsToFetch =
    entity.kind === "vehicle"
      ? [SPECIAL_VEHICLE_ATTRIBUTE]
      : entity.kind === "person"
        ? [SPECIAL_PERSON_ATTRIBUTE]
        : [];

  // Use prefetched facts if available, otherwise fall back to useCardHydration
  // Important: prefetchedFacts can be:
  // - undefined: facts were not requested in the search, so we should hydrate
  // - null: facts were requested but came back empty, so we should NOT hydrate (use null)
  // - object: facts were requested and returned, use them
  const hasPrefetchedFacts = prefetchedFacts !== undefined;
  const shouldUseBatchFetch = !hasPrefetchedFacts && factsToFetch.length > 0;
  const { facts: fetchedFacts, loading } = useCardHydration(
    entity,
    shouldUseBatchFetch ? factsToFetch : []
  );

  // Use prefetched facts first (even if null), then fall back to fetched facts
  // If prefetchedFacts is null, it means facts were requested but empty, so use null
  const facts = hasPrefetchedFacts ? prefetchedFacts || {} : fetchedFacts || {};

  const factSubheader =
    shouldUseBatchFetch && loading ? (
      <span className="text-gray-400">Loading...</span>
    ) : (
      renderFactsSubheader(entity, facts)
    );

  const linkClassName =
    variant === "inline"
      ? "block cursor-pointer no-underline text-inherit"
      : "block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors no-underline text-inherit";

  // The list variant (gaps-v8 §6, #11426): the dropdown-row anatomy the
  // recents list established — a quiet 16px outline icon, list-scale name,
  // fine sub-line — so a typed search and the recents read as one list.
  // The card's 40px tinted circle and heavier name stay for the card and
  // inline variants.
  if (variant === "list") {
    const displayName = entity.display_name || entity.name;
    const listContent = (
      <div className="flex items-center gap-3 min-w-0">
        {!hideIcon && (
          <EntityIcon
            kind={entity.kind}
            size={16}
            className="text-gray-400 flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <ClippedListName name={displayName}>
            {displayName}
            <PendingReviewBadge staging={entity.staging} />
          </ClippedListName>
          <div className="flex items-center gap-1 text-xs text-gray-400 truncate">
            {factSubheader}
            {factSubheader && subheader ? <span> • </span> : null}
            {subheader}
          </div>
        </div>
      </div>
    );
    if (disableLink) return listContent;
    return (
      <Link
        {...prefetchAttrs(entity)}
        to={entityNavigate.prepareLink(entity)}
        className="block cursor-pointer no-underline text-inherit"
      >
        {listContent}
      </Link>
    );
  }

  const content = (
    // No pill and no affiliation tooltip for a person (#11426, keeper ruling
    // A3 + Hunter's ruling): identity is ink, and hover stays quiet — most
    // people are external, so announcing it restated the default.
    <div className="flex items-start gap-3">
      {!hideIcon && (
        <Tooltip title={entityKindMap[entity.kind].label} placement="top">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 flex-shrink-0">
            {entity.kind === "person" ? (
              getInitials(entity.name)
            ) : (
              <EntityIcon kind={entity.kind} className="w-5 h-5" />
            )}
          </div>
        </Tooltip>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <Tooltip title={entity.display_name || entity.name} placement="top">
            <h3
              className={`font-medium text-gray-900 ${hideIcon ? "" : "truncate"}`}
            >
              {entity.display_name || entity.name}
            </h3>
          </Tooltip>
          <PendingReviewBadge staging={entity.staging} />
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-500 truncate">
          {factSubheader}
          {factSubheader && subheader ? <span> • </span> : null}
          {subheader}
        </div>
      </div>
    </div>
  );

  if (disableLink) {
    return content;
  }

  return (
    <Link
      {...prefetchAttrs(entity)}
      to={entityNavigate.prepareLink(entity)}
      className={linkClassName}
    >
      {content}
    </Link>
  );
};

const renderFactsSubheader = (
  entity: ShallowEntity,
  facts: Record<string, any>
) => {
  const renderedFacts = Object.entries(facts)
    .filter(([key, factValue]) => factValue)
    .map(([key, factValue]) => {
      const convertedFactValue = factToFactValue(factValue);
      if (!convertedFactValue) return null;

      // Use generateSubheader for proper fact rendering
      if (key === SPECIAL_PERSON_ATTRIBUTE && entity.kind === "person") {
        return generateSubheader(
          SPECIAL_PERSON_ATTRIBUTE,
          "fact",
          convertedFactValue,
          {
            name: SPECIAL_PERSON_ATTRIBUTE,
            is_hierarchical: false,
            is_required: false,
            scalar_type: "string",
            scalar_unit: null,
            scalar_values: null,
            entity_types: ["company", "manager"],
          } as any,
          true
        );
      }

      if (key === SPECIAL_VEHICLE_ATTRIBUTE && entity.kind === "vehicle") {
        return generateSubheader(
          SPECIAL_VEHICLE_ATTRIBUTE,
          "fact",
          convertedFactValue,
          {
            name: SPECIAL_VEHICLE_ATTRIBUTE,
            is_hierarchical: false,
            is_required: false,
            scalar_type: "string",
            scalar_unit: null,
            scalar_values: null,
            entity_types: null,
          } as any,
          true
        );
      }

      // Fallback for other facts
      return convertedFactValue.scalar || convertedFactValue.entity?.name;
    })
    .filter(Boolean);

  if (renderedFacts.length === 0) return null;

  return (
    <>
      {renderedFacts.map((fact, index) => (
        <React.Fragment key={index}>
          {fact}
          {index < renderedFacts.length - 1 && <span> • </span>}
        </React.Fragment>
      ))}
    </>
  );
};

export default EntityCard;

// Helper function to convert EntityCellValue to FactValue
const factToFactValue = (
  fact?: EntityCellValue | null
): FactValue | undefined => {
  if (!fact) return undefined;
  if (Array.isArray(fact)) {
    return fact[0];
  }
  return fact;
};

const getInitials = (name: string): string => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};
